import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import "./RegisterPage.css";
import Navbar from "../../components/Navbar/Navbar";
import { toast } from "react-toastify";

const API_AUTH_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

const RegisterPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [step, setStep] = useState(1); // 1: Registration Form, 2: Verification
    const [formData, setFormData] = useState({
        username: "",
        email: "",
        password: "",
        confirmPassword: "",
        acceptedTerms: false
    });
    const [verificationCode, setVerificationCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [resendDisabled, setResendDisabled] = useState(false);
    const [countdown, setCountdown] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    // Password Criteria State
    const [criteria, setCriteria] = useState({
        length: false,
        uppercase: false,
        special: false,
        matches: false
    });

    // Password Validation Regex: Min 8 chars, 1 uppercase, 1 special char
    const passwordRegex = /^(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const validateStep1 = () => {
        if (!formData.username || !formData.email || !formData.password) {
            toast.error(t("fill_all_fields", "Preencha todos os campos obrigatórios."));
            return false;
        }
        if (formData.password !== formData.confirmPassword) {
            toast.error(t("passwords_dont_match", "As senhas não coincidem."));
            return false;
        }
        if (!passwordRegex.test(formData.password)) {
            toast.error(t("password_requirements", "A senha deve ter pelo menos 8 caracteres, uma letra maiúscula e um caractere especial."));
            return false;
        }
        if (!formData.acceptedTerms) {
            toast.error(t("must_accept_terms", "Você deve aceitar os termos de uso."));
            return false;
        }
        return true;
    };

    const handleRegister = async (e) => {
        if (e) e.preventDefault();
        if (!validateStep1()) return;

        setLoading(true);
        try {
            await axios.post(`${API_AUTH_URL}/register`, {
                username: formData.username,
                password: formData.password,
                email: formData.email
            });

            toast.success(t("verification_code_sent", "Código de verificação enviado para seu e-mail!"));
            setStep(2);
            startResendTimer();
        } catch (err) {
            toast.error(err.response?.data?.message || t("registration_error", "Erro ao iniciar registro."));
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async (e) => {
        e.preventDefault();
        if (verificationCode.length !== 6) {
            toast.error(t("invalid_code_format", "O código deve ter 6 dígitos."));
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${API_AUTH_URL}/verify`, {
                email: formData.email,
                code: verificationCode,
                acceptedTerms: formData.acceptedTerms
            });

            if (response.status === 201 || response.status === 200) {
                toast.success(t("registration_success", "Cadastro realizado com sucesso! Faça login para continuar."));
                navigate("/login");
            }
        } catch (err) {
            toast.error(err.response?.data?.message || t("verification_error", "Código inválido ou expirado."));
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (resendDisabled) return;

        setLoading(true);
        try {
            await axios.post(`${API_AUTH_URL}/resend-code`, {
                username: formData.username,
                password: formData.password,
                email: formData.email
            });
            toast.success(t("code_resent", "Um novo código foi enviado."));
            startResendTimer();
        } catch (err) {
            toast.error(err.response?.data?.message || t("resend_error", "Erro ao reenviar código."));
        } finally {
            setLoading(false);
        }
    };

    const startResendTimer = () => {
        setResendDisabled(true);
        setCountdown(60);
    };

    useEffect(() => {
        const pass = formData.password;
        setCriteria({
            length: pass.length >= 8,
            uppercase: /[A-Z]/.test(pass),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(pass),
            matches: pass.length > 0 && pass === formData.confirmPassword
        });
    }, [formData.password, formData.confirmPassword]);

    const getStrengthLevel = () => {
        let level = 0;
        if (criteria.length) level++;
        if (criteria.uppercase) level++;
        if (criteria.special) level++;
        // matches doesn't count for complexity/strength per se, but we can evaluate overall
        return level;
    };

    const strengthLabels = [
        { label: t("strength_weak", "Fraca"), color: "#ff4757" },
        { label: t("strength_fair", "Razoável"), color: "#ffa502" },
        { label: t("strength_good", "Boa"), color: "#2ed573" },
        { label: t("strength_strong", "Excelente"), color: "#27d5cf" }
    ];

    const strength = strengthLabels[getStrengthLevel()];

    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setTimeout(() => setCountdown(countdown - 1), 1000);
        } else {
            setResendDisabled(false);
        }
        return () => clearTimeout(timer);
    }, [countdown]);

    return (
        <div className="register-page">
            <Navbar />
            <div className="register-container">
                <div className="register-card">
                    <div className="register-header">
                        <h2>{step === 1 ? t("register_title", "Cadastro") : t("verification_title", "Verificação")}</h2>
                        <p>
                            {step === 1
                                ? t("register_subtitle", "Crie sua conta no NovaInk")
                                : t("verification_subtitle", "Enviamos um código de 6 dígitos para seu e-mail.")}
                        </p>
                    </div>

                    {step === 1 ? (
                        <form onSubmit={handleRegister} className="register-form">
                            <div className="input-group">
                                <label>{t("username", "Usuário")}</label>
                                <input
                                    type="text"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleInputChange}
                                    placeholder={t("username_placeholder", "Nome de usuário")}
                                    required
                                />
                            </div>

                            <div className="input-group">
                                <label>{t("email", "E-mail")}</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    placeholder="exemplo@email.com"
                                    required
                                />
                            </div>

                            <div className="input-group">
                                <label>{t("password", "Senha")}</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        placeholder="••••••••"
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowPassword(!showPassword)}
                                        tabIndex="-1"
                                    >
                                        {showPassword ? (
                                            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                        ) : (
                                            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                        )}
                                    </button>
                                </div>

                                {formData.password && (
                                    <div className="password-strength-meter">
                                        <div className="strength-bar-container">
                                            <div
                                                className="strength-bar-fill"
                                                style={{
                                                    width: `${(getStrengthLevel() + 1) * 25}%`,
                                                    backgroundColor: strength.color
                                                }}
                                            ></div>
                                        </div>
                                        <span className="strength-label" style={{ color: strength.color }}>
                                            {strength.label}
                                        </span>
                                    </div>
                                )}

                                <div className="password-requirements-list">
                                    <div className={`requirement ${criteria.length ? 'met' : ''}`}>
                                        <span className="req-icon">{criteria.length ? '✓' : '○'}</span>
                                        {t("req_min_8", "Mínimo 8 caracteres")}
                                    </div>
                                    <div className={`requirement ${criteria.uppercase ? 'met' : ''}`}>
                                        <span className="req-icon">{criteria.uppercase ? '✓' : '○'}</span>
                                        {t("req_uppercase", "Uma letra maiúscula")}
                                    </div>
                                    <div className={`requirement ${criteria.special ? 'met' : ''}`}>
                                        <span className="req-icon">{criteria.special ? '✓' : '○'}</span>
                                        {t("req_special", "Um caractere especial")}
                                    </div>
                                </div>
                            </div>

                            <div className="input-group">
                                <label>{t("confirm_password", "Confirmar Senha")}</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleInputChange}
                                        placeholder="••••••••"
                                        required
                                    />
                                    <button
                                        type="button"
                                        className="password-toggle"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        tabIndex="-1"
                                    >
                                        {showConfirmPassword ? (
                                            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                        ) : (
                                            <svg viewBox="0 0 24 24" width="20" height="20" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                        )}
                                    </button>
                                </div>
                                {formData.confirmPassword && (
                                    <div className={`password-match-indicator ${criteria.matches ? 'met' : 'failed'}`}>
                                        {criteria.matches ? t("passwords_match", "As senhas coincidem ✓") : t("passwords_mismatch", "Senhas não coincidem ✕")}
                                    </div>
                                )}
                            </div>

                            <div className="input-group">
                                <label className="custom-checkbox" htmlFor="acceptedTerms">
                                    <input
                                        type="checkbox"
                                        id="acceptedTerms"
                                        name="acceptedTerms"
                                        checked={formData.acceptedTerms}
                                        onChange={handleInputChange}
                                        required
                                    />
                                    <span className="checkmark"></span>
                                    <span className="checkbox-text">
                                        {t("accept_terms_label", "Eu aceito os")}{" "}
                                        <Link to="/terms" target="_blank" onClick={(e) => e.stopPropagation()}>
                                            {t("terms_of_service_title", "Termos de Uso")}
                                        </Link>{" "}
                                        {t("and", "e")}{" "}
                                        <Link to="/privacy" target="_blank" onClick={(e) => e.stopPropagation()}>
                                            {t("privacy_policy_title", "Política de Privacidade")}
                                        </Link>
                                    </span>
                                </label>
                            </div>

                            <button type="submit" className="register-btn" disabled={loading}>
                                {loading ? t("loading", "Carregando...") : t("register_button", "Cadastrar")}
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleVerify} className="register-form">
                            <div className="input-group">
                                <label>{t("verification_code", "Código de Verificação")}</label>
                                <input
                                    type="text"
                                    maxLength="6"
                                    value={verificationCode}
                                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ""))}
                                    placeholder="000000"
                                    className="verification-input"
                                    required
                                />
                            </div>

                            <button type="submit" className="register-btn" disabled={loading}>
                                {loading ? t("loading", "Carregando...") : t("verify_button", "Verificar E-mail")}
                            </button>

                            <div className="verification-actions">
                                <button
                                    type="button"
                                    className="resend-btn"
                                    onClick={handleResend}
                                    disabled={resendDisabled || loading}
                                >
                                    {resendDisabled
                                        ? `${t("resend_in", "Reenviar em")} ${countdown}s`
                                        : t("resend_code", "Reenviar código")}
                                </button>
                                <button type="button" className="back-btn" onClick={() => setStep(1)}>
                                    {t("back", "Voltar")}
                                </button>
                            </div>
                        </form>
                    )}

                    <div className="register-footer">
                        <Link to="/login" className="toggle-btn">
                            {t("already_have_account", "Já tem uma conta? Entre aqui")}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegisterPage;
